import { estimateUsdCost, pricingFromUsdPerMillion } from 'tokentally';
import { formatUsd, USD_SMART_CENTS_THRESHOLDS } from '../../lib/formatters/currency';

import { canonicalizeModelId, MODEL_REGISTRY_BY_ID } from './models';
import type { ModelCapability } from './types';

export interface ModelPricing {
  inputPrice: number; // Cost per 1M input tokens
  maxInputTokens: number; // Maximum input tokens
  maxOutputTokens: number; // Maximum output tokens
  outputPrice: number; // Cost per 1M output tokens
  totalContextLength: number; // Total context window
}

/**
 * Pricing is defined for concrete provider model identifiers only.
 * Canonical Sweetistics IDs (e.g. model.reasoning.balanced) are resolved to
 * provider models via resolveProviderModelId/ModelDefinition before pricing lookups.
 */
export const AI_MODEL_PRICING: Record<string, ModelPricing> = {
  'openai/gpt-5': {
    inputPrice: 12,
    outputPrice: 36,
    maxInputTokens: 400_000,
    maxOutputTokens: 400_000,
    totalContextLength: 400_000,
  },
  'openai/gpt-5-mini': {
    inputPrice: 6,
    outputPrice: 18,
    maxInputTokens: 400_000,
    maxOutputTokens: 400_000,
    totalContextLength: 400_000,
  },
  'openai/gpt-5-nano': {
    inputPrice: 2,
    outputPrice: 6,
    maxInputTokens: 400_000,
    maxOutputTokens: 400_000,
    totalContextLength: 400_000,
  },
  // xAI Grok 4 Fast family (kept for compatibility)
  'xai/grok-4-1-fast-reasoning': {
    inputPrice: 0.2,
    outputPrice: 0.5,
    maxInputTokens: 2_000_000,
    maxOutputTokens: 2_000_000,
    totalContextLength: 2_000_000,
  },
  'xai/grok-4-1-fast-non-reasoning': {
    inputPrice: 0.2,
    outputPrice: 0.5,
    maxInputTokens: 2_000_000,
    maxOutputTokens: 2_000_000,
    totalContextLength: 2_000_000,
  },
  'xai/grok-4-fast-reasoning': {
    inputPrice: 0.2,
    outputPrice: 0.5,
    maxInputTokens: 2_000_000,
    maxOutputTokens: 2_000_000,
    totalContextLength: 2_000_000,
  },
  'xai/grok-4-fast-non-reasoning': {
    inputPrice: 0.2,
    outputPrice: 0.5,
    maxInputTokens: 2_000_000,
    maxOutputTokens: 2_000_000,
    totalContextLength: 2_000_000,
  },
  'xai/grok-4': {
    inputPrice: 3,
    outputPrice: 15,
    maxInputTokens: 256_000,
    maxOutputTokens: 32_000,
    totalContextLength: 256_000,
  },
  'xai/grok-code-fast-1': {
    inputPrice: 0.2,
    outputPrice: 1.5,
    maxInputTokens: 256_000,
    maxOutputTokens: 256_000,
    totalContextLength: 256_000,
  },

  // Anthropic Claude 4 Series
  'anthropic/sonnet-4.5': {
    inputPrice: 3,
    outputPrice: 15,
    maxInputTokens: 1_000_000,
    maxOutputTokens: 64_000,
    totalContextLength: 1_000_000,
  },

  // Google Gemini 2.5 Series
  'google/gemini-2.5-pro': {
    inputPrice: 1.25, // Up to 200K tokens, then 2.50
    outputPrice: 10, // Up to 200K tokens, then 15.00
    maxInputTokens: 1_048_576,
    maxOutputTokens: 65_536,
    totalContextLength: 1_048_576,
  },
};

/**
 * Calculate the cost of an API call based on token usage
 */
export function calculateCost(
  model: string,
  inputTokens: number,
  outputTokens: number
): { inputCost: number; outputCost: number; totalCost: number } {
  const pricing = AI_MODEL_PRICING[model];
  if (!pricing) {
    throw new Error(`Unknown model: ${model}`);
  }

  const perToken = pricingFromUsdPerMillion({
    inputUsdPerMillion: pricing.inputPrice,
    outputUsdPerMillion: pricing.outputPrice,
  });
  const cost = estimateUsdCost({
    usage: { inputTokens, outputTokens },
    pricing: perToken,
  });
  if (!cost) {
    throw new Error(`Failed to calculate cost for model: ${model}`);
  }

  return {
    inputCost: cost.inputUsd,
    outputCost: cost.outputUsd,
    totalCost: cost.totalUsd,
  };
}

function resolveDirectPricingKey(candidate: string): string | null {
  if (AI_MODEL_PRICING[candidate]) {
    return candidate;
  }
  const lowered = candidate.toLowerCase();
  const matched = Object.keys(AI_MODEL_PRICING).find((key) => key.toLowerCase() === lowered);
  return matched ?? null;
}

const PRICING_ALIASES: Record<string, string> = {
  'grok-4-1-fast': 'xai/grok-4-1-fast-non-reasoning',
  'grok-4-1-fast-reasoning': 'xai/grok-4-1-fast-reasoning',
  'grok-4-1-fast-non-reasoning': 'xai/grok-4-1-fast-non-reasoning',
  'xai/grok-4-1-fast': 'xai/grok-4-1-fast-non-reasoning',
  'grok-4-fast': 'xai/grok-4-fast-non-reasoning',
  'grok-code-fast-1': 'xai/grok-code-fast-1',
  'google/gemini-latest': 'google/gemini-2.5-pro',
  'gemini-latest': 'google/gemini-2.5-pro',
  'models/gemini-2.5-pro': 'google/gemini-2.5-pro',
  'models/gemini-2.5-pro-latest': 'google/gemini-2.5-pro',
};

function resolvePricingModel(model: string): string {
  const trimmed = typeof model === 'string' ? model.trim() : '';
  if (!trimmed) {
    throw new Error('No pricing model provided');
  }

  const directKey = resolveDirectPricingKey(trimmed);
  if (directKey) {
    return directKey;
  }

  const aliasTarget = PRICING_ALIASES[trimmed.toLowerCase()];
  if (aliasTarget) {
    const aliasKey = resolveDirectPricingKey(aliasTarget);
    if (aliasKey) {
      return aliasKey;
    }
  }

  const canonical = canonicalizeModelId(trimmed);
  if (canonical) {
    const canonicalKey = resolveDirectPricingKey(canonical);
    if (canonicalKey) {
      return canonicalKey;
    }
    const definition = MODEL_REGISTRY_BY_ID[canonical];
    if (definition) {
      const providerCandidate = definition.providerModelId?.trim();
      if (providerCandidate) {
        const providerKey = resolveDirectPricingKey(providerCandidate);
        if (providerKey) {
          return providerKey;
        }
      }
    }
  }

  const definition = MODEL_REGISTRY_BY_ID[trimmed];
  if (definition) {
    const providerId = (definition.providerModelId ?? trimmed).trim();
    const providerKey = resolveDirectPricingKey(providerId);
    if (providerKey) {
      return providerKey;
    }
  }

  throw new Error(`No pricing configured for model: ${model}`);
}

export function calculateCostCredits(
  model: string,
  inputTokens: number,
  outputTokens: number
): { inputCost: number; outputCost: number; totalCost: number; pricingModel: string } {
  const pricingModel = resolvePricingModel(model);
  const { inputCost, outputCost, totalCost } = calculateCost(pricingModel, inputTokens, outputTokens);

  return {
    inputCost: Math.round(inputCost * 100),
    outputCost: Math.round(outputCost * 100),
    totalCost: Math.round(totalCost * 100),
    pricingModel,
  };
}

/**
 * Format cost in USD with appropriate precision
 */
export function formatCost(cost: number): string {
  return formatUsd(cost, {
    thresholds: USD_SMART_CENTS_THRESHOLDS,
    defaultFractionDigits: 4,
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
    useGrouping: false,
  });
}

/**
 * Get the most cost-effective model for a given token count
 */
export function getCheapestModel(
  inputTokens: number,
  outputTokens: number,
  requiredCapability?: ModelCapability
): string {
  let eligibleModels = Object.entries(AI_MODEL_PRICING);

  // Filter by token requirements
  eligibleModels = eligibleModels.filter(
    ([, pricing]) => pricing.maxInputTokens >= inputTokens && pricing.maxOutputTokens >= outputTokens
  );

  // Filter by capability requirements
  const capabilityFilters: Partial<
    Record<
      ModelCapability,
      (models: [string, (typeof AI_MODEL_PRICING)[string]][]) => [string, (typeof AI_MODEL_PRICING)[string]][]
    >
  > = {
    reasoning: (models) => {
      const reasoningModels = new Set([
        'xai/grok-4-1-fast-reasoning',
        'xai/grok-4-fast-reasoning',
        'xai/grok-4',
        'openai/gpt-5',
        'anthropic/sonnet-4.5',
      ]);
      return models.filter(([model]) => {
        if (reasoningModels.has(model)) {
          return true;
        }
        if ((model.includes('grok-4-fast') || model.includes('grok-4-1-fast')) && model.includes('reasoning')) {
          return true;
        }
        if (model.includes('sonnet') || model.includes('opus')) {
          return true;
        }
        return false;
      });
    },
    'long-context': (models) =>
      models.filter(([, pricing]) => {
        // Filter to models with 1M+ context windows
        return pricing.totalContextLength >= 1_000_000;
      }),
    speed: (models) =>
      models.filter(
        ([model]) =>
          model.includes('grok-4-fast') ||
          model.includes('grok-4-1-fast') ||
          model.includes('nano') ||
          model.includes('flash')
      ),
  };

  const capabilityFilter = requiredCapability === undefined ? undefined : capabilityFilters[requiredCapability];
  if (capabilityFilter) {
    eligibleModels = capabilityFilter(eligibleModels);
  }

  // Calculate costs and sort by total cost
  const modelCosts = eligibleModels.map(([model]) => ({
    model,
    cost: calculateCost(model, inputTokens, outputTokens).totalCost,
  }));

  modelCosts.sort((a, b) => a.cost - b.cost);

  return modelCosts[0]?.model || 'xai/grok-4-1-fast-non-reasoning';
}

/**
 * Compare costs between different models
 */
export function compareModelCosts(
  inputTokens: number,
  outputTokens: number
): Array<{ model: string; cost: number; savings: number }> {
  const costs = Object.keys(AI_MODEL_PRICING).map((model) => ({
    model,
    cost: calculateCost(model, inputTokens, outputTokens).totalCost,
    savings: 0,
  }));

  costs.sort((a, b) => a.cost - b.cost);

  // Calculate savings compared to most expensive
  const maxCost = Math.max(...costs.map((c) => c.cost));
  for (const c of costs) {
    c.savings = ((maxCost - c.cost) / maxCost) * 100;
  }

  return costs;
}
